/**
 * usePresenceHeartbeat — the portal SPA's leg of the presence beat behind the
 * home page's Online rail. Mounted ONCE at the App root (the visit-tracking
 * precedent), it beats `heartbeatPresence` on a timer for as long as a
 * signed-in user has the tab in the foreground and has interacted recently.
 *
 * WHEN to beat is the shared {@link startPresenceBeat} policy (visible tabs
 * only, so a window left open overnight decays to "last seen …" like a closed
 * one). This hook adds the two portal-specific rules:
 *
 *  - Signed-in only. A logged-out (or anonymous) visitor beats nothing; the
 *    callable would reject them anyway, and the rail lists people, not tabs.
 *  - The portal beat NEVER carries a `gameKey`. Games launch standalone
 *    (playProxy / `/games/<slug>/`), so the runtime that IS the game owns the
 *    in-game beat — @djgg/game-client for natives, @djgg/creation-shell for
 *    creations. A portal tab claiming "in game" because it once linked to one
 *    is exactly the lie the rail must not tell.
 *
 * Fire-and-forget: presence is decoration, and a failed beat must never
 * surface to the user or break navigation.
 */
import { useEffect } from 'react';
import { startPresenceBeat } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';

export function usePresenceHeartbeat(): void {
  const services = useServices();
  const { user } = useAuth();
  const uid = user?.uid;
  const anonymous = user?.isAnonymous ?? false;

  useEffect(() => {
    if (!uid || anonymous) return;
    return startPresenceBeat(() => {
      services.callable.heartbeatPresence({}).catch(() => {
        /* presence is decoration — never surface a failed beat */
      });
    });
  }, [services, uid, anonymous]);
}
